import type {
  HubAlgorithmDefinition,
  HubJobDefinition,
  HubJobKind,
} from '@robovision/hub'
import { arrayToObject } from '@robovision/rvai-utils'
import { JobAnnotationArtifact } from './artifacts/JobAnnotationArtifact'
import { JobBlobArtifact } from './artifacts/JobBlobArtifact'
import { JobDatapoolArtifact } from './artifacts/JobDatapoolArtifact'
import { JobDatasetArtifact } from './artifacts/JobDatasetArtifact'
import { JobMetricArtifact } from './artifacts/JobMetricArtifact'
import { JobModelArtifact } from './artifacts/JobModelArtifact'
import { JobParameterArtifact } from './artifacts/JobParameterArtifact'
import {
  JobTaggableArtifact,
  JobTaggableArtifactType,
} from './artifacts/JobTaggableArtifact'
import { BaseDefinition } from './BaseDefinition'
import { JobRuntimeParameters } from './common/JobRuntimeParameters'


export class JobDefinition extends BaseDefinition<HubJobDefinition> {
  public readonly blobArtifacts: Record<string, JobBlobArtifact>
  public readonly datapoolArtifacts: Record<string, JobDatapoolArtifact>
  public readonly datasetArtifacts: Record<string, JobDatasetArtifact>
  public readonly exampleArtifacts: Record<string, JobTaggableArtifact>
  public readonly annotationArtifacts: Record<string, JobAnnotationArtifact>
  public readonly metricArtifacts: Record<string, JobMetricArtifact>
  public readonly modelArtifacts: Record<string, JobModelArtifact>
  public readonly parameterArtifacts: Record<string, JobParameterArtifact>
  public readonly sampleArtifacts: Record<string, JobTaggableArtifact>
  public readonly tagArtifacts: Record<string, JobTaggableArtifact>

  public constructor (jobDefinition: HubJobDefinition, definition: HubAlgorithmDefinition) {
    super(jobDefinition, definition)
    this.annotationArtifacts = this.initAnnotationArtifacts()
    this.blobArtifacts = this.initBlobArtifacts(definition)
    this.datapoolArtifacts = this.initDatapoolArtifacts(definition)
    this.datasetArtifacts = this.initDatasetArtifacts()
    this.exampleArtifacts = this.initExampleArtifacts()
    this.metricArtifacts = this.initMetricArtifacts(definition)
    this.modelArtifacts = this.initModelArtifacts()
    this.parameterArtifacts = this.initParameterArtifacts(definition)
    this.sampleArtifacts = this.initSampleArtifacts()
    this.tagArtifacts = this.initTagArtifacts()
  }

  public buildDefaultResources (): JobRuntimeParameters {
    return JobRuntimeParameters.fromRuntimeParameters(
      this.job.runtime,
    )
  }

  public get kind (): HubJobKind {
    return this.job.kind
  }

  private initAnnotationArtifacts () {
    return arrayToObject(
      this.job.artifacts?.annotations ?? [],
      artifact => artifact.id,
      artifact => JobAnnotationArtifact.fromDefinition(artifact),
    )
  }

  private initBlobArtifacts (algorithmDefinition: HubAlgorithmDefinition) {
    return arrayToObject(
      this.job.artifacts?.blobs ?? [],
      artifact => artifact.id,
      artifact => JobBlobArtifact.fromDefinition(artifact, algorithmDefinition),
    )
  }

  private initDatapoolArtifacts (algorithmDefinition: HubAlgorithmDefinition) {
    return arrayToObject(
      this.job.artifacts?.datapools ?? [],
      artifact => artifact.id,
      artifact => JobDatapoolArtifact.fromDefinition(artifact, algorithmDefinition),
    )
  }

  private initDatasetArtifacts () {
    return arrayToObject(
      this.job.artifacts?.datasets ?? [],
      artifact => artifact.id,
      artifact => JobDatasetArtifact.fromDefinition(artifact),
    )
  }

  private initExampleArtifacts () {
    return arrayToObject(
      this.job.artifacts?.examples ?? [],
      artifact => artifact.id,
      artifact => JobTaggableArtifact.fromDefinition(artifact, JobTaggableArtifactType.Example),
    )
  }

  private initMetricArtifacts (algorithmDefinition: HubAlgorithmDefinition) {
    return arrayToObject(
      this.job.artifacts?.metrics ?? [],
      artifact => artifact.id,
      artifact => JobMetricArtifact.fromDefinition(artifact, algorithmDefinition),
    )
  }

  private initModelArtifacts () {
    return arrayToObject(
      this.job.artifacts?.models ?? [],
      artifact => artifact.id,
      artifact => JobModelArtifact.fromDefinition(artifact),
    )
  }

  private initParameterArtifacts (algorithmDefinition: HubAlgorithmDefinition) {
    return arrayToObject(
      this.job.artifacts?.parameters ?? [],
      artifact => artifact.id,
      artifact => JobParameterArtifact.fromDefinition(artifact, algorithmDefinition),
    )
  }

  private initSampleArtifacts () {
    return arrayToObject(
      this.job.artifacts?.samples ?? [],
      artifact => artifact.id,
      artifact => JobTaggableArtifact.fromDefinition(artifact, JobTaggableArtifactType.Sample),
    )
  }

  private initTagArtifacts () {
    return arrayToObject(
      this.job.artifacts?.tags ?? [],
      artifact => artifact.id,
      artifact => JobTaggableArtifact.fromDefinition(artifact, JobTaggableArtifactType.Tag),
    )
  }
}
